import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Search, MoreHorizontal, Edit, Trash2, FileText, Package, Receipt, ClipboardList, Eye, Tag, CheckCircle2, Ship, Plane, FileCheck, Building2, X, Filter, Download, Upload, FileSpreadsheet, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const statusLabels = {
  documentacion_inicial: "Documentación inicial",
  en_planta: "En la planta",
  en_reconocimiento: "En reconocimiento",
  listo_para_cargar: "Listo para cargar",
  en_transito: "En tránsito",
  en_destino: "En destino",
  entregado_cliente: "Entregado a cliente",
  completado: "Completado",
  anulado: "Anulado",
  rechazado: "Rechazado",
  otros: "Otros",
  // Estados antiguos (compatibilidad)
  pendiente: "Documentación inicial",
  recibido: "En destino",
};

const statusColors = {
  documentacion_inicial: "bg-amber-100 text-amber-800",
  en_planta: "bg-orange-100 text-orange-800",
  en_reconocimiento: "bg-yellow-100 text-yellow-800",
  listo_para_cargar: "bg-lime-100 text-lime-800",
  en_transito: "bg-sky-100 text-sky-800",
  en_destino: "bg-cyan-100 text-cyan-800",
  entregado_cliente: "bg-teal-100 text-teal-800",
  completado: "bg-emerald-100 text-emerald-800",
  anulado: "bg-gray-100 text-gray-800",
  rechazado: "bg-red-100 text-red-800",
  otros: "bg-purple-100 text-purple-800",
  // Estados antiguos (compatibilidad)
  pendiente: "bg-amber-100 text-amber-800",
  recibido: "bg-cyan-100 text-cyan-800",
};

// Formatear fecha SIN conversión de zona horaria
const formatDateSafe = (dateStr) => {
  if (!dateStr) return "-";
  const datePart = dateStr.split('T')[0];
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  }
  return dateStr;
};

export default function Batches() {
  const [batches, setBatches] = useState([]);
  const [batchDocStatus, setBatchDocStatus] = useState({}); // Estado de documentos por lote
  const [batchDocuments, setBatchDocuments] = useState({}); // Documentos por lote
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [showAllCompanies, setShowAllCompanies] = useState(false); // Ver todas las empresas
  const [importing, setImporting] = useState(false); // Estado de importación Excel
  
  // Estados para selección y eliminación masiva
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [showDeleteMultipleDialog, setShowDeleteMultipleDialog] = useState(false);
  const [deletingMultiple, setDeletingMultiple] = useState(false);
  
  // Estados para manejo de duplicados en importación
  const [showDuplicatesDialog, setShowDuplicatesDialog] = useState(false);
  const [duplicatesInfo, setDuplicatesInfo] = useState(null);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  
  // Estado para ordenación
  const [sortConfig, setSortConfig] = useState({ field: "created_at", direction: "desc" });
  
  // Filtro para ocultar terminados
  const [ocultarTerminados, setOcultarTerminados] = useState(true);
  
  // Filtros
  const [filters, setFilters] = useState({
    cliente: "",
    proveedor: "",
    producto: "",
    estado: "",
  });
  
  // Listas para los filtros
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  
  const { hasRole, user } = useAuth();
  const navigate = useNavigate();

  // Tipos de documentos que el cliente puede ver
  const clientVisibleDocs = [
    'factura_cliente', 'packing_list', 'etiqueta', 'bl', 'awb',
    'certificado_sanitario', 'certificado_captura', 'cso'
  ];

  // Descargar plantilla Excel
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/batches/template-excel", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "plantilla_importar_lotes.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Plantilla descargada correctamente");
    } catch (error) {
      toast.error("Error al descargar la plantilla");
    }
  };

  // Importar lotes desde Excel
  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("El archivo debe ser un Excel (.xlsx o .xls)");
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      // Primero verificar si hay duplicados (mode=check)
      const checkResponse = await api.post("/batches/import-excel?mode=check", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const checkData = checkResponse.data;
      
      if (checkData.duplicados > 0) {
        // Hay duplicados, mostrar diálogo para que el usuario decida
        setDuplicatesInfo(checkData);
        setPendingImportFile(file);
        setShowDuplicatesDialog(true);
        setImporting(false);
        event.target.value = "";
        return;
      }
      
      // No hay duplicados, importar directamente (mode=skip es equivalente)
      await executeImport(file, "skip");

    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar el archivo");
      setImporting(false);
    }
    // Limpiar el input
    event.target.value = "";
  };

  // Ejecutar la importación con un modo específico
  const executeImport = async (file, mode) => {
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(`/batches/import-excel?mode=${mode}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { exitosos, fallidos, actualizados, saltados, errores } = response.data;

      if (exitosos > 0) {
        toast.success(`${exitosos} lote(s) importado(s) correctamente`);
      }
      if (actualizados > 0) {
        toast.success(`${actualizados} lote(s) actualizado(s)`);
      }
      if (saltados > 0) {
        toast.info(`${saltados} lote(s) saltados (ya existían)`);
      }
      if (fallidos > 0) {
        toast.error(`${fallidos} lote(s) fallaron al importar`);
        console.error("Errores de importación:", errores);
      }

      // Recargar la lista de lotes
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar el archivo");
    } finally {
      setImporting(false);
      setShowDuplicatesDialog(false);
      setDuplicatesInfo(null);
      setPendingImportFile(null);
    }
  };

  // Manejar decisión del usuario sobre duplicados
  const handleDuplicatesDecision = async (decision) => {
    if (!pendingImportFile) return;
    
    if (decision === "skip") {
      // Solo importar los nuevos
      await executeImport(pendingImportFile, "skip");
    } else if (decision === "overwrite") {
      // Sobrescribir todos los existentes
      await executeImport(pendingImportFile, "overwrite");
    } else {
      // Cancelar
      setShowDuplicatesDialog(false);
      setDuplicatesInfo(null);
      setPendingImportFile(null);
    }
  };

  useEffect(() => {
    fetchBatches();
    fetchFilterData();
  }, [showAllCompanies]);

  const fetchFilterData = async () => {
    try {
      const [clientsRes, suppliersRes, productsRes] = await Promise.all([
        api.get("/clients"),
        api.get("/suppliers"),
        api.get("/products"),
      ]);
      setClients(clientsRes.data);
      setSuppliers(suppliersRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  };

  const fetchBatches = async () => {
    try {
      setLoading(true);
      // Si admin tiene marcado ver todas las empresas, añadir parámetro
      const params = hasRole("admin") && showAllCompanies ? "?all_companies=true" : "";
      const response = await api.get(`/batches${params}`);
      setBatches(response.data);
      
      // Verificar estado de documentos para cada lote
      const docStatusPromises = response.data.map(async (batch) => {
        try {
          const docsRes = await api.get(`/documents?numero_lote=${batch.numero_lote}`);
          const docs = docsRes.data;
          
          // Documentos requeridos para el check verde
          const requiredDocs = [
            'factura_origen',
            'packing_list',
            'cso',
            'certificado_captura',
            'certificado_exportacion',
            'bl'
          ];
          
          // Verificar qué documentos tiene
          const hasFacturaOrigen = docs.some(d => d.tipo_documento === 'factura_origen');
          const hasPackingList = docs.some(d => d.tipo_documento === 'packing_list');
          const hasEtiquetas = docs.some(d => d.tipo_documento?.startsWith('etiqueta'));
          const hasCSO = docs.some(d => d.tipo_documento === 'cso');
          const hasCertCaptura = docs.some(d => d.tipo_documento === 'certificado_captura');
          const hasCertExportacion = docs.some(d => d.tipo_documento === 'certificado_exportacion');
          const hasBL = docs.some(d => d.tipo_documento === 'bl');
          
          const isComplete = hasFacturaOrigen && hasPackingList && hasEtiquetas && 
                            hasCSO && hasCertCaptura && hasCertExportacion && hasBL;
          
          return { batchId: batch.id, isComplete, docs };
        } catch {
          return { batchId: batch.id, isComplete: false, docs: [] };
        }
      });
      
      const statuses = await Promise.all(docStatusPromises);
      const statusMap = {};
      const docsMap = {};
      statuses.forEach(s => { 
        statusMap[s.batchId] = s.isComplete;
        docsMap[s.batchId] = s.docs;
      });
      setBatchDocStatus(statusMap);
      setBatchDocuments(docsMap);
      
    } catch (error) {
      toast.error("Error al cargar los lotes");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/batches/${deleteId}`);
      toast.success("Lote eliminado correctamente");
      setBatches(batches.filter((b) => b.id !== deleteId));
    } catch (error) {
      toast.error("Error al eliminar el lote");
    } finally {
      setDeleteId(null);
    }
  };

  // Función para abrir/descargar un documento
  const handleOpenDocument = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: doc.tipo_archivo || "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error) {
      toast.error("Error al abrir el documento");
    }
  };

  // Obtener documentos visibles para el usuario según su rol
  const getVisibleDocuments = (batchId) => {
    const docs = batchDocuments[batchId] || [];
    if (hasRole("admin") || hasRole("admin_consultor")) {
      return docs;
    }
    if (hasRole("cliente")) {
      return docs.filter(d => clientVisibleDocs.some(type => d.tipo_documento?.includes(type)));
    }
    // Proveedor: todos excepto factura_cliente
    return docs.filter(d => !d.tipo_documento?.includes('factura_cliente'));
  };

  // Labels para tipos de documento
  const docTypeLabels = {
    factura_origen: "Factura Origen",
    factura_cliente: "Factura Cliente",
    packing_list: "Packing List",
    etiqueta: "Etiquetas",
    bl: "BL",
    awb: "AWB",
    cso: "CSO",
    certificado_captura: "Cert. Captura",
    certificado_sanitario: "Cert. Sanitario",
    certificado_exportacion: "Cert. Exportación",
    dua: "DUA",
    otros: "Otros",
  };

  const getDocLabel = (tipoDoc) => {
    for (const [key, label] of Object.entries(docTypeLabels)) {
      if (tipoDoc?.includes(key)) return label;
    }
    return tipoDoc || "Documento";
  };

  // Funciones para selección y eliminación masiva (solo admin)
  const isAdmin = hasRole("admin");
  
  const handleSelectBatch = (batchId, checked) => {
    if (checked) {
      setSelectedBatches(prev => [...prev, batchId]);
    } else {
      setSelectedBatches(prev => prev.filter(id => id !== batchId));
    }
  };
  
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedBatches(sortedBatches.map(b => b.id));
    } else {
      setSelectedBatches([]);
    }
  };
  
  const handleDeleteMultiple = async () => {
    if (selectedBatches.length === 0) return;
    
    setDeletingMultiple(true);
    try {
      const response = await api.post("/batches/delete-multiple", selectedBatches);
      toast.success(`${response.data.eliminados} lotes eliminados correctamente`);
      if (response.data.fallidos > 0) {
        toast.warning(`${response.data.fallidos} lotes no pudieron eliminarse`);
      }
      setSelectedBatches([]);
      setShowDeleteMultipleDialog(false);
      fetchBatches();
    } catch (error) {
      toast.error("Error al eliminar los lotes");
    } finally {
      setDeletingMultiple(false);
    }
  };

  const filteredBatches = batches.filter((batch) => {
    // Ocultar terminados (completado, anulado, rechazado)
    if (ocultarTerminados) {
      const estadosOcultos = ["completado", "anulado", "rechazado"];
      if (estadosOcultos.includes(batch.estado)) return false;
    }
    
    // Aplicar filtros de dropdown primero (ignorar si es "" o "all")
    if (filters.cliente && filters.cliente !== "all" && batch.cliente_id !== filters.cliente) return false;
    if (filters.proveedor && filters.proveedor !== "all" && batch.proveedor_id !== filters.proveedor) return false;
    if (filters.producto && filters.producto !== "all" && batch.producto_id !== filters.producto) return false;
    if (filters.estado && filters.estado !== "all" && batch.estado !== filters.estado) return false;
    
    // Luego aplicar búsqueda de texto
    const searchLower = search.toLowerCase();
    if (!searchLower) return true; // Si no hay búsqueda, ya pasó los filtros
    
    // Campos básicos que todos pueden buscar
    const basicMatch = 
      batch.numero_lote?.toLowerCase().includes(searchLower) ||
      batch.producto_nombre?.toLowerCase().includes(searchLower) ||
      batch.contenedor?.toLowerCase().includes(searchLower) ||
      batch.empresa_nombre?.toLowerCase().includes(searchLower);
    
    // Campos adicionales según rol
    if (hasRole(["admin", "admin_consultor"])) {
      return basicMatch ||
        batch.cliente_nombre?.toLowerCase().includes(searchLower) ||
        batch.proveedor_nombre?.toLowerCase().includes(searchLower) ||
        batch.dua?.toLowerCase().includes(searchLower) ||
        batch.certificado_exportacion?.toLowerCase().includes(searchLower) ||
        batch.bl_maritimo?.toLowerCase().includes(searchLower) ||
        batch.awb_aereo?.toLowerCase().includes(searchLower) ||
        batch.factura_origen?.toLowerCase().includes(searchLower) ||
        batch.factura_destino?.toLowerCase().includes(searchLower);
    }
    if (hasRole("proveedor")) {
      return basicMatch ||
        batch.dua?.toLowerCase().includes(searchLower) ||
        batch.certificado_exportacion?.toLowerCase().includes(searchLower) ||
        batch.bl_maritimo?.toLowerCase().includes(searchLower) ||
        batch.factura_origen?.toLowerCase().includes(searchLower);
    }
    if (hasRole("cliente")) {
      return basicMatch ||
        batch.cliente_nombre?.toLowerCase().includes(searchLower) ||
        batch.bl_maritimo?.toLowerCase().includes(searchLower);
    }
    return basicMatch;
  });

  // Función para cambiar el orden
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  // Icono de ordenación para las cabeceras
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    }
    return sortConfig.direction === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  // Ordenar los lotes filtrados
  const sortedBatches = [...filteredBatches].sort((a, b) => {
    const { field, direction } = sortConfig;
    let aVal = a[field];
    let bVal = b[field];
    
    // Manejar valores nulos/undefined
    if (aVal == null) aVal = "";
    if (bVal == null) bVal = "";
    
    // Para fechas, comparar como strings (formato ISO)
    if (field === "created_at" || field === "llegada_estimada" || field === "fecha_tentativa_embarque") {
      aVal = aVal || "";
      bVal = bVal || "";
    }
    
    // Para texto, comparar en minúsculas
    if (typeof aVal === "string") aVal = aVal.toLowerCase();
    if (typeof bVal === "string") bVal = bVal.toLowerCase();
    
    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });

  // Calcular suma total del peso neto de los lotes filtrados
  const totalPesoNeto = sortedBatches.reduce((sum, batch) => sum + (batch.peso_neto || 0), 0);

  const clearFilters = () => {
    setFilters({ cliente: "", proveedor: "", producto: "", estado: "" });
    setSearch("");
  };

  const hasActiveFilters = 
    (filters.cliente && filters.cliente !== "all") || 
    (filters.proveedor && filters.proveedor !== "all") || 
    (filters.producto && filters.producto !== "all") || 
    (filters.estado && filters.estado !== "all") || 
    search;

  return (
    <div className="space-y-6" data-testid="batches-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lotes</h1>
          <p className="text-slate-500 mt-1">Gestión de lotes de pescado congelado</p>
        </div>
        {hasRole(["admin", "proveedor"]) && (
          <div className="flex items-center gap-2 flex-wrap">
            {/* Botón descargar plantilla */}
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              className="text-slate-600"
              data-testid="download-template-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Plantilla Excel
            </Button>
            
            {/* Botón importar Excel */}
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportExcel}
                className="hidden"
                disabled={importing}
                data-testid="import-excel-input"
              />
              <Button
                variant="outline"
                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                disabled={importing}
                asChild
              >
                <span>
                  {importing ? (
                    <>
                      <div className="spinner w-4 h-4 mr-2" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Importar Excel
                    </>
                  )}
                </span>
              </Button>
            </label>
            
            {/* Botón nuevo lote */}
            <Button
              onClick={() => navigate("/batches/new")}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="create-batch-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Lote
            </Button>
          </div>
        )}
      </div>
      
      {/* Barra de selección masiva - Solo admin */}
      {isAdmin && selectedBatches.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-red-800 font-medium">
            {selectedBatches.length} lote{selectedBatches.length !== 1 ? "s" : ""} seleccionado{selectedBatches.length !== 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedBatches([])}
              className="text-slate-600"
            >
              <X className="w-4 h-4 mr-1" />
              Deseleccionar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowDeleteMultipleDialog(true)}
              data-testid="delete-multiple-button"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Eliminar seleccionados
            </Button>
          </div>
        </div>
      )}

      {/* Barra de búsqueda y filtros */}
      <div className="space-y-3">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por lote, producto, contenedor, DUA..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-batches"
            />
          </div>
          {/* Checkbox para ver todas las empresas - Solo admin */}
          {hasRole("admin") && (
            <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded border border-blue-200">
              <Checkbox
                id="all-companies"
                checked={showAllCompanies}
                onCheckedChange={(checked) => setShowAllCompanies(checked)}
              />
              <Label htmlFor="all-companies" className="text-sm text-blue-800 cursor-pointer flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                Ver todas las empresas
              </Label>
            </div>
          )}
          
          {/* Checkbox para ocultar terminados */}
          <div className="flex items-center gap-2 bg-amber-50 px-3 py-2 rounded border border-amber-200">
            <Checkbox
              id="ocultar-terminados"
              checked={ocultarTerminados}
              onCheckedChange={(checked) => setOcultarTerminados(checked)}
            />
            <Label htmlFor="ocultar-terminados" className="text-sm text-amber-800 cursor-pointer">
              Ocultar terminados
            </Label>
          </div>
        </div>

        {/* Filtros desplegables */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-slate-500">
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">Filtros:</span>
          </div>
          
          {/* Filtro por Producto - visible para todos */}
          <Select
            value={filters.producto}
            onValueChange={(value) => setFilters({ ...filters, producto: value })}
          >
            <SelectTrigger className="w-[180px]" data-testid="filter-producto">
              <SelectValue placeholder="Producto" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los productos</SelectItem>
              {products.map((product) => (
                <SelectItem key={product.id} value={product.id}>
                  {product.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtro por Cliente - solo admin y admin_consultor */}
          {hasRole(["admin", "admin_consultor"]) && (
            <Select
              value={filters.cliente}
              onValueChange={(value) => setFilters({ ...filters, cliente: value })}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-cliente">
                <SelectValue placeholder="Cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los clientes</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Filtro por Proveedor - solo admin y admin_consultor */}
          {hasRole(["admin", "admin_consultor"]) && (
            <Select
              value={filters.proveedor}
              onValueChange={(value) => setFilters({ ...filters, proveedor: value })}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-proveedor">
                <SelectValue placeholder="Proveedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los proveedores</SelectItem>
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.id}>
                    {supplier.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Filtro por Estado - visible para todos */}
          <Select
            value={filters.estado}
            onValueChange={(value) => setFilters({ ...filters, estado: value })}
          >
            <SelectTrigger className="w-[180px]" data-testid="filter-estado">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.entries(statusLabels)
                .filter(([key]) => !["pendiente", "recibido"].includes(key)) // Excluir estados de compatibilidad
                .map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          {/* Botón para limpiar filtros */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-slate-500 hover:text-slate-700"
              data-testid="clear-filters"
            >
              <X className="w-4 h-4 mr-1" />
              Limpiar
            </Button>
          )}
        </div>
      </div>

      {/* Indicador de suma total de peso neto */}
      {!loading && sortedBatches.length > 0 && (
        <div className="flex items-center gap-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">
              Total Peso Neto:
            </span>
            <span className="text-lg font-bold text-blue-900" data-testid="total-peso-neto">
              {totalPesoNeto.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kg
            </span>
          </div>
          <span className="text-sm text-blue-600">
            ({sortedBatches.length} {sortedBatches.length === 1 ? 'lote' : 'lotes'})
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : sortedBatches.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-sm border border-slate-200">
          <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No hay lotes</h3>
          <p className="text-slate-500 mb-4">
            {search ? "No se encontraron lotes con ese criterio" : "Comienza creando tu primer lote"}
          </p>
          {hasRole(["admin", "proveedor"]) && !search && (
            <Button onClick={() => navigate("/batches/new")} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Crear Lote
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="data-table">
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider w-[40px]">
                  {isAdmin && (
                    <Checkbox
                      checked={selectedBatches.length === sortedBatches.length && sortedBatches.length > 0}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  )}
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Nº Lote
                </TableHead>
                {/* Empresa: solo visible cuando se muestran todas las empresas */}
                {showAllCompanies && hasRole("admin") && (
                  <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                    Empresa
                  </TableHead>
                )}
                <TableHead 
                  className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider cursor-pointer hover:bg-slate-200 select-none"
                  onClick={() => handleSort("producto_nombre")}
                >
                  <div className="flex items-center">
                    Producto
                    <SortIcon field="producto_nombre" />
                  </div>
                </TableHead>
                {/* Cliente: oculto para proveedores */}
                {!hasRole("proveedor") && (
                  <TableHead 
                    className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider cursor-pointer hover:bg-slate-200 select-none"
                    onClick={() => handleSort("cliente_nombre")}
                  >
                    <div className="flex items-center">
                      Cliente
                      <SortIcon field="cliente_nombre" />
                    </div>
                  </TableHead>
                )}
                {/* Proveedor: visible para admin, admin_consultor, y proveedores con múltiples empresas */}
                {(hasRole(["admin", "admin_consultor"]) || (hasRole("proveedor") && user?.proveedor_ids?.length > 1)) && (
                  <TableHead 
                    className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider cursor-pointer hover:bg-slate-200 select-none"
                    onClick={() => handleSort("proveedor_nombre")}
                  >
                    <div className="flex items-center">
                      Proveedor
                      <SortIcon field="proveedor_nombre" />
                    </div>
                  </TableHead>
                )}
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Peso Neto
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Contenedor
                </TableHead>
                <TableHead 
                  className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider cursor-pointer hover:bg-slate-200 select-none"
                  onClick={() => handleSort("fecha_tentativa_embarque")}
                >
                  <div className="flex items-center">
                    Fecha Embarque
                    <SortIcon field="fecha_tentativa_embarque" />
                  </div>
                </TableHead>
                <TableHead 
                  className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider cursor-pointer hover:bg-slate-200 select-none"
                  onClick={() => handleSort("estado")}
                >
                  <div className="flex items-center">
                    Estado
                    <SortIcon field="estado" />
                  </div>
                </TableHead>
                <TableHead 
                  className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider cursor-pointer hover:bg-slate-200 select-none"
                  onClick={() => handleSort("llegada_estimada")}
                >
                  <div className="flex items-center">
                    Llegada Est.
                    <SortIcon field="llegada_estimada" />
                  </div>
                </TableHead>
                <TableHead 
                  className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider cursor-pointer hover:bg-slate-200 select-none"
                  onClick={() => handleSort("created_at")}
                >
                  <div className="flex items-center">
                    Fecha
                    <SortIcon field="created_at" />
                  </div>
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider w-[50px]">
                  
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedBatches.map((batch) => (
                <TableRow 
                  key={batch.id} 
                  className={`hover:bg-slate-50 cursor-pointer ${selectedBatches.includes(batch.id) ? 'bg-red-50' : ''}`}
                  data-testid={`batch-row-${batch.id}`}
                  onClick={() => navigate(`/batches/${batch.id}/view`)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {isAdmin ? (
                      <Checkbox
                        checked={selectedBatches.includes(batch.id)}
                        onCheckedChange={(checked) => handleSelectBatch(batch.id, checked)}
                        data-testid={`select-batch-${batch.id}`}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell className="font-mono font-medium">
                    <div className="flex items-center gap-2">
                      {batch.documentacion_completa && (
                        <CheckCircle2 
                          className="w-4 h-4 text-green-600 shrink-0" 
                          title="Documentación completa para operador logístico"
                        />
                      )}
                      <span>{batch.numero_lote}</span>
                    </div>
                  </TableCell>
                  {/* Empresa: solo visible cuando se muestran todas las empresas */}
                  {showAllCompanies && hasRole("admin") && (
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {batch.empresa_nombre || "Sin asignar"}
                      </Badge>
                    </TableCell>
                  )}
                  <TableCell>{batch.producto_nombre || "-"}</TableCell>
                  {/* Cliente: oculto para proveedores */}
                  {!hasRole("proveedor") && (
                    <TableCell>{batch.cliente_nombre || "-"}</TableCell>
                  )}
                  {/* Proveedor: visible para admin, admin_consultor, y proveedores con múltiples empresas */}
                  {(hasRole(["admin", "admin_consultor"]) || (hasRole("proveedor") && user?.proveedor_ids?.length > 1)) && (
                    <TableCell>{batch.proveedor_nombre || "-"}</TableCell>
                  )}
                  <TableCell className="font-mono">{batch.peso_neto ? `${batch.peso_neto} kg` : "-"}</TableCell>
                  <TableCell className="font-mono text-xs">{batch.contenedor || "-"}</TableCell>
                  <TableCell className="text-slate-500">
                    {formatDateSafe(batch.fecha_tentativa_embarque)}
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[batch.estado]}>
                      {statusLabels[batch.estado]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {formatDateSafe(batch.llegada_estimada)}
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {formatDateSafe(batch.created_at)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`batch-menu-${batch.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem onClick={() => navigate(`/batches/${batch.id}/view`)}>
                          <Eye className="w-4 h-4 mr-2" />
                          Consultar
                        </DropdownMenuItem>
                        {/* Editar: solo admin y proveedor */}
                        {hasRole(["admin", "proveedor"]) && (
                          <DropdownMenuItem onClick={() => navigate(`/batches/${batch.id}`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                        )}
                        
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs text-slate-500">Generar documentos</DropdownMenuLabel>
                        
                        {/* Factura Origen: solo admin y proveedor */}
                        {hasRole(["admin", "proveedor"]) && (
                          <DropdownMenuItem onClick={() => navigate(`/batches/${batch.id}/invoice-origin`)}>
                            <Receipt className="w-4 h-4 mr-2" />
                            Factura Origen
                          </DropdownMenuItem>
                        )}
                        {/* Factura Cliente: solo admin y cliente */}
                        {hasRole(["admin", "cliente"]) && (
                          <DropdownMenuItem onClick={() => navigate(`/batches/${batch.id}/invoice-client`)}>
                            <Receipt className="w-4 h-4 mr-2" />
                            Factura Cliente
                          </DropdownMenuItem>
                        )}
                        {/* Packing List y Etiquetas: todos */}
                        <DropdownMenuItem onClick={() => navigate(`/batches/${batch.id}/packing-list`)}>
                          <ClipboardList className="w-4 h-4 mr-2" />
                          Packing List
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/batches/${batch.id}/labels`)}>
                          <Tag className="w-4 h-4 mr-2" />
                          Etiquetas
                        </DropdownMenuItem>
                        
                        {/* Documentos subidos */}
                        {getVisibleDocuments(batch.id).length > 0 && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-xs text-slate-500">Documentos subidos</DropdownMenuLabel>
                            {getVisibleDocuments(batch.id).map((doc) => (
                              <DropdownMenuItem 
                                key={doc.id} 
                                onClick={() => handleOpenDocument(doc)}
                                className="text-emerald-700"
                              >
                                <FileCheck className="w-4 h-4 mr-2" />
                                {getDocLabel(doc.tipo_documento)}
                              </DropdownMenuItem>
                            ))}
                          </>
                        )}
                        
                        {hasRole("admin") && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => setDeleteId(batch.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar lote?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el lote y toda su información.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Diálogo de eliminación masiva */}
      <AlertDialog open={showDeleteMultipleDialog} onOpenChange={setShowDeleteMultipleDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar {selectedBatches.length} lotes?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán permanentemente los {selectedBatches.length} lotes seleccionados junto con todos sus documentos y facturas asociadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingMultiple}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMultiple}
              className="bg-red-600 hover:bg-red-700"
              disabled={deletingMultiple}
            >
              {deletingMultiple ? (
                <>
                  <div className="spinner w-4 h-4 mr-2" />
                  Eliminando...
                </>
              ) : (
                `Eliminar ${selectedBatches.length} lotes`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Diálogo de duplicados en importación */}
      <AlertDialog open={showDuplicatesDialog} onOpenChange={(open) => !open && handleDuplicatesDecision("cancel")}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-amber-500" />
              Se encontraron lotes duplicados
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  El archivo contiene <strong>{duplicatesInfo?.total_en_excel || 0}</strong> lotes:
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-green-50 p-2 rounded border border-green-200">
                    <span className="font-semibold text-green-700">{duplicatesInfo?.nuevos || 0}</span> nuevos
                  </div>
                  <div className="bg-amber-50 p-2 rounded border border-amber-200">
                    <span className="font-semibold text-amber-700">{duplicatesInfo?.duplicados || 0}</span> ya existen
                  </div>
                </div>
                
                {duplicatesInfo?.lista_duplicados?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-500 mb-1">Lotes existentes:</p>
                    <div className="max-h-32 overflow-y-auto bg-slate-50 rounded p-2 text-xs">
                      {duplicatesInfo.lista_duplicados.slice(0, 10).map((dup, idx) => (
                        <div key={idx} className="flex justify-between py-0.5 border-b border-slate-200 last:border-0">
                          <span className="font-mono">{dup.numero_lote}</span>
                          <span className="text-slate-500">{dup.producto}</span>
                        </div>
                      ))}
                      {duplicatesInfo.lista_duplicados.length > 10 && (
                        <div className="text-slate-400 pt-1">...y {duplicatesInfo.lista_duplicados.length - 10} más</div>
                      )}
                    </div>
                  </div>
                )}
                
                <p className="text-sm font-medium pt-2">¿Qué deseas hacer?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => handleDuplicatesDecision("cancel")}>
              Cancelar
            </AlertDialogCancel>
            <Button 
              variant="outline" 
              onClick={() => handleDuplicatesDecision("skip")}
              className="border-green-300 text-green-700 hover:bg-green-50"
            >
              Solo importar nuevos ({duplicatesInfo?.nuevos || 0})
            </Button>
            <Button 
              variant="default"
              onClick={() => handleDuplicatesDecision("overwrite")}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Sobrescribir existentes ({duplicatesInfo?.duplicados || 0})
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
